import { useCallback, useEffect } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'
import { FILE_STALE_TIME } from '@/constants/file'

import { SweetspotError } from '@/helpers/error'

import { filesQueryKeys } from '@/hooks/files/types'
import { useSetFileCache } from '@/hooks/files/useFile'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { OpportunityType } from '@/types/Opportunity'

interface UseOpportunityFilesArgs<T> {
  opportunityId: UUID | ''
  opportunityType: OpportunityType
  enabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  select?: (data: PaginatedResponse<File>) => T
}

export const fetchOpportunityFiles = async (
  opportunityId: UUID | '',
  opportunityType: OpportunityType,
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<File>> => {
  if (accessToken === null || accessToken === undefined || !opportunityId) {
    return { count: 0, has_more: false, data: [] }
  }

  let url = `${API_URL}/api/v1/files/opportunity`
  const queryParams = new URLSearchParams()
  queryParams.set('opportunity_id', opportunityId)
  queryParams.set('opportunity_type', opportunityType)
  if (pagination.limit) {
    queryParams.set('limit', pagination.limit.toString())
  }
  if (pagination.offset) {
    queryParams.set('offset', pagination.offset.toString())
  }
  url = `${url}?${queryParams.toString()}`

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to fetch opportunity files: ${res.statusText}`,
      cause: res,
    })
  }
  return (await res.json()) as PaginatedResponse<File>
}

export const useOpportunityFiles = <T = File[]>({
  opportunityId,
  opportunityType,
  enabled = true,
  staleTime = FILE_STALE_TIME,
  refetchOnMount = true,
  refetchOnWindowFocus = true,
  select,
}: UseOpportunityFilesArgs<T>) => {
  const { accessToken } = useAuthInfo()

  const setFileCache = useSetFileCache()

  const fetchFn = useCallback(async () => {
    const data = await fetchOpportunityFiles(
      opportunityId,
      opportunityType,
      accessToken,
      {
        limit: 10000,
      },
    )
    setFileCache(data.data)
    return data
  }, [opportunityId, opportunityType, accessToken, setFileCache])

  const selectFn = useCallback(
    (data: PaginatedResponse<File>) => {
      if (select) {
        return select(data)
      }
      return data.data.sort((a, b) =>
        a.name.localeCompare(b.name),
      ) as unknown as T
    },
    [select],
  )

  const query = useQuery({
    queryFn: fetchFn,
    queryKey: filesQueryKeys.opportunity({ opportunityId, opportunityType }),
    staleTime,
    refetchOnMount,
    refetchOnWindowFocus,
    enabled: enabled && !!opportunityId,
    select: selectFn,
  })

  return query
}

interface UseOpportunityFilesPrefetchArgs {
  opportunityId: UUID | ''
  opportunityType: OpportunityType
}

export const useOpportunityFilesPrefetch = ({
  opportunityId,
  opportunityType,
}: UseOpportunityFilesPrefetchArgs) => {
  const { accessToken } = useAuthInfo()

  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: filesQueryKeys.opportunity({ opportunityId, opportunityType }),
      queryFn: () =>
        fetchOpportunityFiles(opportunityId, opportunityType, accessToken, {
          limit: 10000,
        }),
    })
  }, [opportunityId, opportunityType, accessToken, queryClient])
}
